import {
  ChartUserOverrides,
  DashboardLayout,
  DataTableUserOverrides,
  WebDashboardContentSpec,
} from "@coeff/agent-types";
import { FeatureFlag } from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import React, { useCallback, useMemo, useState } from "react";

import { DashboardWindow, useDashboardWindowGlobals } from "./dashboardHelpers";
import { TableContentHeightReport } from "./dataTableSizing";
import { DateRangeSelection } from "./dateRangeSelection";
import { DraggableBlocksSection } from "./DraggableBlocksSection";
import { FilterDefaultsSnapshot } from "./slicerDefaults";
import { SlicersSection } from "./SlicersSection";
import { markAllSelections } from "./slicerValues";
import { ErrorStateDetails } from "./useBlockRender";
import { useDashboardConsoleCapture } from "./useDashboardConsoleCapture";
import { RunSQLFn, SlicerValue, useDashboardRuntime } from "./useDashboardRuntime";
import { useWebDashboardSentryTag } from "./webDashboardPerf";

interface Props {
  contentSpec: WebDashboardContentSpec;
  runSQL: RunSQLFn;
  targetWindow: DashboardWindow;
  idSuffix?: string;
  initialLayout?: DashboardLayout;
  onLayoutChange: (layout: DashboardLayout) => void;
  onBlockDelete: (blockId: string, newLayout: DashboardLayout) => void;
  /** Optional for public dashboards where Explain is disabled */
  onExplainBlockInChat?: (
    blockId: string,
    errorMessage?: string,
    errorStateDetails?: ErrorStateDetails | null
  ) => void;
  /** Open chat and explain the block (instant if a stored explanation exists). */
  onExplainInChat?: (blockId: string) => void;
  onEditBlock?: (blockId: string, updates: import("./BlockEditPopover").BlockEditUpdates) => void;
  /** Cache-only write for the popover's controls; saved by onCommitBlockEdits. */
  onApplyBlockEditLocally?: (
    blockId: string,
    updates: import("./BlockEditPopover").BlockEditUpdates
  ) => void;
  /** Ends a block's popover session: one save for everything applied locally. */
  onCommitBlockEdits?: (blockId: string) => void;
  onAddBlock?: (
    blockType: import("./AddBlockButton").AddBlockType,
    insertAtRowIndex: number,
    prompt?: string
  ) => void;
  canEdit: boolean;
  /** See DraggableBlocksSection Props — hides resize handles while an AI edit runs. */
  isAiEditing?: boolean;
  maxRows?: number;
  onResetSlicers?: () => void;
  onSlicerChange?: (slicerName: string, slicerType: string) => void;
  areAiImagesStale?: boolean;
  /** Block IDs that should show the glowing rim effect while an AI edit is in-flight */
  refreshingBlockIds?: Set<string>;
  onBlockRendered?: (blockId: string) => void;
  /** When false, SQLite DB is not yet ready — show all blocks as skeletons immediately */
  isSQLiteReady?: boolean;
  /** Called when a block's "Fix this" button is clicked after a render failure */
  onFixBlock?: (blockId: string, errorMessage: string) => void;
  /** Hide slicer controls while keeping slicer runtime state available to blocks. */
  hideSlicers?: boolean;
  /** When true, dashboard is still being built — slicers are shown but non-interactive */
  isBuilding?: boolean;
  /** Email of the currently logged-in viewer (null for anonymous/public dashboards) */
  userEmail?: string | null;
  /** Dashboard id, used to tag perf instrumentation so it's filterable in Sentry. */
  dashboardId?: string;
  /**
   * Editor-only: persist a data table's manual column adjustments (drag-resize /
   * drag-reorder) into the block's `table_overrides`. Omit for view-only users —
   * their drags stay session-scoped.
   */
  onTableOverridesChange?: (blockId: string, overrides: DataTableUserOverrides) => void;
  /**
   * Editor-only: persist the current filter state as what the dashboard opens on. Omit for
   * view-only users — the "Save as default" affordance is then not rendered.
   */
  onSaveSlicerDefaults?: (snapshot: FilterDefaultsSnapshot) => Promise<void>;
}

// --------------------------------
// Extra CSS Styles
// --------------------------------

const ExtraStyles: React.FC = () => (
  <style>{`
    .chart-container {
      position: relative;
    }

    .chart-canvas {
      position: absolute;
      top: 0;
      left: 0;
      width: 100% !important;
      height: 100% !important;
    }

    /* Keep the sort arrow hugged to the header text (not justified to the far edge)
       and let the text ellipsize rather than the sort chrome destroying the header. */
    .coeff-data-table-grid .ag-header-cell-label {
      justify-content: flex-start;
      gap: 2px;
    }
    .coeff-data-table-grid .ag-header-cell-text {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      flex: 0 1 auto;
    }

    /* Right-align a numeric header by pushing the whole label (text + sort arrow)
       to the end, so the arrow stays hugged to the text. Do NOT stretch the text to
       width:100% — that shoves the sort arrow to the far edge and forces the header
       to truncate ("Lookalike Sco…    ↑"). Grid-prefixed to outrank the rule above. */
    .coeff-data-table-grid .coeff-ag-right-aligned-header .ag-header-cell-label {
      justify-content: flex-end;
    }
    .coeff-data-table-grid .coeff-ag-right-aligned-header .ag-header-cell-text {
      text-align: right;
      margin-right: 4px;
    }

    /* Slimmer sort affordance: a compact arrow, no multi-sort order badge taking
       up header width. AG Grid renders .ag-icon as a background-image sized by the
       --ag-icon-size var (NOT font-size), so shrink it via that var + box size. */
    .coeff-data-table-grid .ag-sort-indicator-container {
      margin-left: 2px;
    }
    .coeff-data-table-grid .ag-sort-indicator-icon {
      padding-left: 2px;
    }
    .coeff-data-table-grid .ag-sort-indicator-icon .ag-icon {
      --ag-icon-size: 13px;
      width: 13px;
      height: 13px;
      background-size: 13px 13px;
      font-size: 13px;
    }
    .coeff-data-table-grid .ag-sort-order {
      display: none;
    }

    /* Total (pinned bottom) row: a stronger top border to separate it from the
       data, no filled background, and no gap above it. Conditional formatting is
       stripped in JS (see dashboardHelpers). Scoped to a present Total row: the
       empty container stays in flow, and a border on it adds 2px the grid sizing
       doesn't count — a stray vertical scrollbar on Windows. */
    .coeff-data-table-grid .ag-floating-bottom:has(.ag-row) {
      border-top: 2px solid var(--theme--color-card-border, rgba(0, 0, 0, 0.18));
    }
    .coeff-data-table-grid .ag-floating-bottom,
    .coeff-data-table-grid .ag-floating-bottom .ag-row,
    .coeff-data-table-grid .ag-floating-bottom .ag-cell {
      background: transparent !important;
    }

    /* Normalize cell text: one base size for cell content so tables don't render
       at several different sizes. Cells opt into their own sizing via explicit
       inline styles (e.g. pills) which still win. */
    .coeff-data-table-grid .ag-cell {
      font-size: 12px;
    }

    /* Drill-down modals only: hold every text cell to a single ellipsized line.
       AG Grid's own nowrap/ellipsis lands on its .ag-cell-value span, so a custom
       cellRenderer returning block HTML (see wrapCellRendererForAgGrid) can render
       taller than the row and bleed into its neighbour - the "jumbled" text of
       ENG-7527. The full value is revealed by the hover/tap panel instead.
       Standalone table blocks are deliberately untouched. */
    .data-table-modal .coeff-data-table-grid .ag-cell,
    .data-table-modal .coeff-data-table-grid .ag-cell-value,
    .data-table-modal .coeff-data-table-grid .ag-cell-value > * {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* End-of-table footer under infinite scroll (drill-down modals only —
       standalone blocks show the count inline with the card title instead). */
    .coeff-data-table-footer {
      flex-shrink: 0;
      padding: 6px 2px 0;
      font-size: 11px;
      text-align: right;
      opacity: 0.55;
    }

    /* Row count for standalone table blocks: absolutely anchored to the title
       row's right edge, centered on the TITLE's line box (28px = text-lg line
       height; the row can also contain the description paragraph, so top: 50%
       of the row would drift off the title line) — adds zero card height.
       Fades out on block hover, when the top-right action icons overlay claims
       that corner. pointer-events: none so it never blocks those icons
       mid-fade. */
    .block-title-row {
      position: relative;
    }
    .coeff-data-table-row-count {
      position: absolute;
      right: 0;
      top: 0;
      line-height: 28px;
      font-size: 11px;
      font-weight: 400;
      opacity: 0.55;
      white-space: nowrap;
      pointer-events: none;
      transition: opacity 150ms;
    }
    /* Fade on the group/block WRAPPER's hover, not just the card's: the top-right
       action overlay is a z-10 SIBLING of the card inside that wrapper, so moving
       onto the icons clears the card's :hover (the overlay isn't a card descendant)
       while the icons stay visible — a card-only selector fades the count back in
       underneath them. The card selector stays for renders without the wrapper. */
    .group\\/block:hover .coeff-data-table-row-count,
    .themed-dashboard-card:hover .coeff-data-table-row-count,
    .coeff-block-actions-pinned .coeff-data-table-row-count {
      opacity: 0;
    }

    /* Mobile: no hover exists and the title row becomes a flex row (truncating
       title + inline description icon), so an absolute overlay would paint over
       them — let the count participate in the flex layout at the row's end
       instead. Matches the max-sm breakpoint (640px). */
    @media (max-width: 639px) {
      .coeff-data-table-row-count {
        position: static;
        flex-shrink: 0;
        margin-left: auto;
      }
    }

    /* Link affordance (the link column option). The UNDERLINE is the at-rest signal
       (ENG-8483): it used to be the hover-only icon below, which left a linked column
       indistinguishable from a plain one until hovered — no affordance at all on touch,
       and none in any screenshot path (Slack unfurls, thumbnails, html2canvas capture).
       The icon stays as a hover enhancement.

       color/text-decoration are set explicitly because neither iframe defines a global
       a{} rule, so without them the anchor falls back to UA blue + its own underline. */
    .coeff-data-table-grid .coeff-cell-link {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      color: inherit;
      text-decoration: none;
      /* Dragging a link starts a link-drag, which would eat drag-select over the cell
         (the grid runs with enableCellTextSelection). */
      -webkit-user-drag: none;
    }
    /* The text owns the underline, not the anchor: text-decoration inherits into
       descendants and a descendant cannot cancel it, so underlining the anchor would
       also underline the icon glyph. */
    .coeff-data-table-grid .coeff-cell-link-text {
      text-decoration: underline;
      /* Plain fallback first, for anything that cannot parse color-mix (the screenshot
         path rasterises this CSS too). Muted at rest so the underline reads as an
         affordance rather than shouting; full strength on the link itself below. */
      text-decoration-color: currentColor;
      text-decoration-color: color-mix(in srgb, currentColor 45%, transparent);
      text-underline-offset: 2px;
    }
    /* Scoped to the ANCHOR, not the cell: the anchor is the click target, so pointing at
       some other part of the cell is not an intent to follow the link.
       No focus rule here, deliberately. A cell-level :focus-within left the emphasis stuck
       on after a click, because AG Grid focuses the cell you clicked and keeps it focused
       once the pointer is long gone. An anchor-level :focus-visible was tried instead and
       removed as dead CSS: AG Grid's tab navigation moves between .ag-cell elements and
       then out of the grid, so the anchor never receives keyboard focus and the rule could
       not fire. (That the link is unreachable by keyboard at all is a real gap, but a
       pre-existing one that CSS here cannot fix.) */
    .coeff-data-table-grid .coeff-cell-link:hover .coeff-cell-link-text {
      text-decoration-color: currentColor;
    }
    .coeff-data-table-grid .coeff-cell-link-icon {
      font-size: 14px;
      line-height: 1;
      text-decoration: none;
      color: inherit;
      opacity: 0;
      transition: opacity 120ms ease;
    }
    /* Cell hover only — no :focus-within, for the same reason the underline has no focus
       rule: AG Grid keeps the cell you clicked focused, so a focus trigger left the icon
       showing indefinitely after the pointer had moved on. Hover is the whole signal, so
       the affordance appears and disappears with the pointer. */
    .coeff-data-table-grid .ag-cell:hover .coeff-cell-link-icon {
      opacity: 0.7;
    }
    .coeff-data-table-grid .coeff-cell-link-icon:hover {
      opacity: 1;
    }

    /* Frozen (pinned) column divider: hidden until the table is actually
       scrolled horizontally (dashboardHelpers toggles .coeff-hscrolled on the
       grid container from AG Grid's body-scroll events), and drawn on body
       rows only — never through the header. Transparent (not none) in the
       resting state so cells don't shift 1px when the divider appears. */
    .coeff-data-table-grid .ag-pinned-left-header {
      border-right: none !important;
    }
    .coeff-data-table-grid .ag-pinned-right-header {
      border-left: none !important;
    }
    /* NO vertical lines anywhere in the header. AG Grid draws them from three
       places: the pinned-header border (above), each header cell's ::after
       column separator, and the column-resize handle's resting line. Kill the
       first two outright; the resize line only appears while hovering the
       header cell, so resizing stays discoverable without a permanent line. */
    .coeff-data-table-grid .ag-header-cell::after,
    .coeff-data-table-grid .ag-header-group-cell::after {
      border-left: none !important;
      border-right: none !important;
    }
    .coeff-data-table-grid .ag-header-cell-resize::after {
      background-color: transparent !important;
    }
    .coeff-data-table-grid .ag-header-cell:hover .ag-header-cell-resize::after {
      background-color: var(--ag-header-column-resize-handle-color, rgba(0, 0, 0, 0.2)) !important;
    }
    .coeff-data-table-grid .ag-cell.ag-cell-last-left-pinned {
      border-right: 1px solid transparent !important;
    }
    .coeff-data-table-grid .ag-cell.ag-cell-first-right-pinned {
      border-left: 1px solid transparent !important;
    }
    .coeff-data-table-grid.coeff-hscrolled .ag-cell.ag-cell-last-left-pinned {
      border-right: var(--ag-pinned-column-border, 1px solid rgba(0, 0, 0, 0.18)) !important;
    }
    .coeff-data-table-grid.coeff-hscrolled .ag-cell.ag-cell-first-right-pinned {
      border-left: var(--ag-pinned-column-border, 1px solid rgba(0, 0, 0, 0.18)) !important;
    }

    /* Inline header rename: a pencil that fades in on header hover (same
       treatment as the filter/menu buttons below) and an editor input that
       replaces the header text in place. */
    .coeff-data-table-grid .coeff-header-rename-btn {
      border: none;
      background: none;
      padding: 0;
      margin-left: 4px;
      flex: none;
      font-size: 14px;
      line-height: 1;
      color: inherit;
      cursor: pointer;
      opacity: 0;
      transition: opacity 120ms ease;
    }
    .coeff-data-table-grid .ag-header-cell:hover .coeff-header-rename-btn,
    .coeff-data-table-grid .ag-header-cell:focus-within .coeff-header-rename-btn {
      opacity: 0.55;
    }
    .coeff-data-table-grid .coeff-header-rename-btn:hover {
      opacity: 1;
    }
    .coeff-data-table-grid .coeff-header-rename-input {
      width: 100%;
      min-width: 60px;
      font: inherit;
      color: inherit;
      background: transparent;
      border: none;
      border-bottom: 1px solid currentColor;
      border-radius: 0;
      outline: none;
      padding: 0;
    }

    /* Mobile drill-down "Load more" mode replaces AG Grid's paging bar with a custom footer
       (see createDataTableInternal), so hide the built-in one regardless of page count. */
    .coeff-data-table-grid.coeff-data-table-load-more .ag-paging-panel {
      display: none !important;
    }

    /* Mobile: the paging bar (row/page summary + first/prev/next/last controls) is wider than a
       phone-width grid; without wrapping it overflows to the right and scrolls the whole page
       horizontally. Let it wrap onto multiple lines instead. */
    @media (max-width: 639.98px) {
      .coeff-data-table-grid .ag-paging-panel,
      .coeff-data-table-grid .ag-paging-panel-content {
        flex-wrap: wrap;
        justify-content: center;
        height: auto;
        row-gap: 2px;
        padding-left: 4px;
        padding-right: 4px;
      }

      /* Page-level guard: dense AG-Grid tables have several intrinsic narrow-width overflow points
         (paging bar, ag-header-allow-overflow header cells, wide columns) that would scroll the whole
         page sideways on a phone. clip stops page-level horizontal scroll without creating a scroll
         container (vertical scroll & position:sticky unaffected); each table keeps its OWN internal
         horizontal scroll (its .ag-body-viewport), and fixed drawers aren't clipped. */
      #iframe-root {
        overflow-x: clip;
      }
    }

    .coeff-data-table-grid .ag-header-cell-filter-button,
    .coeff-data-table-grid .ag-header-cell-menu-button {
      opacity: 0;
      width: 0;
      min-width: 0;
      margin-left: 0;
      overflow: hidden;
      pointer-events: none;
      transition: opacity 120ms ease, width 120ms ease, margin-left 120ms ease;
    }

    .coeff-data-table-grid .ag-header-cell:hover .ag-header-cell-filter-button,
    .coeff-data-table-grid .ag-header-cell:hover .ag-header-cell-menu-button,
    .coeff-data-table-grid .ag-header-cell:focus-within .ag-header-cell-filter-button,
    .coeff-data-table-grid .ag-header-cell:focus-within .ag-header-cell-menu-button,
    .coeff-data-table-grid .ag-header-cell-filter-button.ag-filter-active,
    .coeff-data-table-grid .ag-header-cell-menu-button.ag-filter-active {
      opacity: 1;
      width: 18px;
      min-width: 18px;
      margin-left: 4px;
      pointer-events: auto;
    }
  `}</style>
);

// --------------------------------
// Main Component
// --------------------------------

export const WebDashboardContent: React.FC<Props> = ({
  contentSpec,
  runSQL,
  targetWindow,
  idSuffix,
  initialLayout,
  onLayoutChange,
  onBlockDelete,
  onExplainBlockInChat,
  onExplainInChat,
  onEditBlock,
  onApplyBlockEditLocally,
  onCommitBlockEdits,
  onAddBlock,
  canEdit,
  isAiEditing,
  maxRows,
  onResetSlicers,
  onSlicerChange,
  areAiImagesStale,
  refreshingBlockIds,
  onBlockRendered,
  isSQLiteReady = true,
  onFixBlock,
  hideSlicers = false,
  isBuilding = false,
  userEmail = null,
  dashboardId,
  onTableOverridesChange,
  onSaveSlicerDefaults,
}) => {
  const effectiveIdSuffix = idSuffix ?? "";

  // Tag the Sentry scope so this dashboard's errors/replays/transactions are filterable.
  useWebDashboardSentryTag(dashboardId);

  // Install console/error capture in the iframe so we can ship runtime failures to the editor agent.
  const { value: isDashboardAutoFixEnabled } = useFeatureGate(
    FeatureFlag.EnableWebDashboardAutofix
  );
  useDashboardConsoleCapture(canEdit && isDashboardAutoFixEnabled ? targetWindow : null);

  const {
    slicerState,
    defaultSlicerState,
    allTimeDateRanges,
    resolvedOptions,
    dateRangeSelections,
    setSlicerValue,
    resetSlicers,
    isSlicersActive,
    isInitialized: isRuntimeInitialized,
    slicerInitErrors,
  } = useDashboardRuntime({
    contentSpec,
    runSQL,
    targetWindow,
    enabled: isSQLiteReady,
    materializeTempViews: true,
    userEmail,
    dashboardId,
  });

  // Both SQLite and runtime must be ready before blocks render real content
  const isInitialized = isSQLiteReady && isRuntimeInitialized;

  // Wrap resetSlicers with analytics tracking
  const handleResetSlicers = useCallback(() => {
    onResetSlicers?.(); // Track analytics if provided
    resetSlicers(); // Then reset slicers
  }, [onResetSlicers, resetSlicers]);

  // Wrap setSlicerValue with analytics tracking
  const handleSetSlicerValue = useCallback(
    (name: string, value: SlicerValue, selection?: DateRangeSelection | null) => {
      // Find the slicer to get its type
      const slicer = contentSpec.slicers?.find(s => s.name === name);
      if (slicer) {
        onSlicerChange?.(name, slicer.type);
      }

      // Then set the value
      setSlicerValue(name, value, selection);
    },
    [onSlicerChange, setSlicerValue, contentSpec.slicers]
  );

  // Editor-only: hand the current filter state up to be written into the dashboard's spec.
  // The caller owns the save (and the optimistic update that follows), so the resulting spec
  // comes back down as new props rather than being tracked locally here.
  const handleSaveSlicersAsDefault = useMemo(() => {
    if (!onSaveSlicerDefaults) {
      return undefined;
    }
    return () => onSaveSlicerDefaults({ slicerState, dateRangeSelections, resolvedOptions });
  }, [onSaveSlicerDefaults, slicerState, dateRangeSelections, resolvedOptions]);

  // Persisted manual column adjustments per data_table block, handed to
  // createDataTable through the window-globals module state. The fingerprint
  // captures the state session drags are layered over (persisted overrides +
  // the render JS that defines the columns): when it changes — AI edit, another
  // editor's save — that block's session memory is invalidated so a stale drag
  // can't shadow the new state. Memoized so the globals effect doesn't re-run
  // on unrelated renders.
  const tableOverrides = useMemo(() => {
    const byBlockId: Record<string, DataTableUserOverrides> = {};
    const fingerprintByBlockId: Record<string, string> = {};
    for (const block of contentSpec.blocks ?? []) {
      if (block.type !== "data_table") {
        continue;
      }
      const overrides =
        "table_overrides" in block && block.table_overrides ? block.table_overrides : null;
      if (overrides) {
        byBlockId[block.block_id] = overrides;
      }
      const renderFunctionJs = "render_function_js" in block ? block.render_function_js : "";
      fingerprintByBlockId[block.block_id] = `${JSON.stringify(overrides)}|${renderFunctionJs}`;
    }
    return { byBlockId, fingerprintByBlockId, onChange: onTableOverridesChange };
  }, [contentSpec.blocks, onTableOverridesChange]);

  // Persisted manual chart adjustments per chart block, handed to createChart through the
  // window-globals module state (the chart analogue of tableOverrides above — no session
  // layer or fingerprints, since charts re-render from scratch on every overrides change).
  const chartOverridesByBlockId = useMemo(() => {
    const byBlockId: Record<string, ChartUserOverrides> = {};
    for (const block of contentSpec.blocks ?? []) {
      if (block.type === "chart" && block.chart_overrides) {
        byBlockId[block.block_id] = block.chart_overrides;
      }
    }
    return byBlockId;
  }, [contentSpec.blocks]);

  // Measured content heights per data table block ({natural, hugged}), reported
  // by the grid after each sizing pass. Table-only layout rows size to them via
  // computeEffectiveRowHeight — collapse to short content, grow-to-fit near
  // misses, hug the snapped grid on overflow — with the stored height untouched
  // as the cap/intent. The 2px guard breaks report → row-resize → re-measure
  // feedback loops.
  const [tableContentHeights, setTableContentHeights] = useState<
    Record<string, TableContentHeightReport>
  >({});
  const handleTableContentHeight = useCallback(
    (blockId: string, heights: TableContentHeightReport) => {
      setTableContentHeights(prev => {
        const previous = prev[blockId];
        if (
          previous !== undefined &&
          Math.abs(previous.natural - heights.natural) <= 2 &&
          Math.abs(previous.hugged - heights.hugged) <= 2
        ) {
          return prev;
        }
        return { ...prev, [blockId]: heights };
      });
    },
    []
  );

  // Expose helper functions globally on the iframe window. setSlicerValue lets custom_html
  // blocks drive slicers programmatically (e.g. quick-filter buttons) via the same path as
  // the native slicer UI.
  useDashboardWindowGlobals(
    runSQL,
    targetWindow,
    contentSpec.js_helper_functions,
    userEmail,
    handleSetSlicerValue,
    dashboardId,
    tableOverrides,
    handleTableContentHeight,
    chartOverridesByBlockId
  );

  // The state render functions receive: each multi-select value that covers every resolved
  // option carries the non-enumerable "all" marker, so `slicerClause` emits no filter and rows
  // with a NULL in the column stay in. The picker keeps the plain state. Memoised so array
  // identity only changes when the state or the options do.
  const blockSlicerState = useMemo(
    () => markAllSelections(slicerState, resolvedOptions),
    [slicerState, resolvedOptions]
  );

  return (
    <>
      <ExtraStyles />

      {!hideSlicers && (
        <SlicersSection
          slicers={contentSpec.slicers ?? []}
          slicerState={slicerState}
          defaultSlicerState={defaultSlicerState}
          allTimeDateRanges={allTimeDateRanges}
          resolvedOptions={resolvedOptions}
          setSlicerValue={handleSetSlicerValue}
          resetSlicers={handleResetSlicers}
          isSlicersActive={isSlicersActive}
          targetWindow={targetWindow}
          idSuffix={effectiveIdSuffix}
          slicerInitErrors={slicerInitErrors}
          isDisabled={!isInitialized}
          onFixBlock={onFixBlock}
          viewerEmail={userEmail}
          canEdit={canEdit}
          onSaveAsDefault={canEdit ? handleSaveSlicersAsDefault : undefined}
        />
      )}

      <DraggableBlocksSection
        blocks={contentSpec.blocks}
        slicerState={blockSlicerState}
        targetWindow={targetWindow}
        idSuffix={effectiveIdSuffix}
        initialLayout={initialLayout}
        onLayoutChange={onLayoutChange}
        onBlockDelete={onBlockDelete}
        onExplainBlockInChat={onExplainBlockInChat}
        onExplainInChat={onExplainInChat}
        onEditBlock={onEditBlock}
        onApplyBlockEditLocally={onApplyBlockEditLocally}
        onCommitBlockEdits={onCommitBlockEdits}
        onAddBlock={onAddBlock}
        canEdit={canEdit}
        isAiEditing={isAiEditing}
        maxRows={maxRows}
        areAiImagesStale={areAiImagesStale}
        refreshingBlockIds={refreshingBlockIds}
        onRendered={onBlockRendered}
        isRuntimeInitialized={isInitialized}
        onFixBlock={onFixBlock}
        tableContentHeights={tableContentHeights}
      />
    </>
  );
};

export default WebDashboardContent;
